'use client';

import type { LucideIcon } from 'lucide-react';
import { Code, Lightbulb, PenTool, Rocket } from 'lucide-react';

interface ProcessStep {
  icon: LucideIcon;
  step: number;
  title: string;
  text: string;
}

const steps: ProcessStep[] = [
  {
    icon: Lightbulb,
    step: 1,
    title: 'Discovery',
    text: 'We align on goals, scope, and a clear roadmap',
  },
  {
    icon: PenTool,
    step: 2,
    title: 'Design',
    text: 'System architecture, UX wireframes, and tech decisions',
  },
  {
    icon: Code,
    step: 3,
    title: 'Build',
    text: 'Iterative development with regular demos and feedback',
  },
  {
    icon: Rocket,
    step: 4,
    title: 'Launch & Scale',
    text: 'Production deployment, monitoring, and ongoing support',
  },
];

function TimelineStep({ icon: Icon, step, title, text }: ProcessStep) {
  return (
    <div className="group relative flex flex-row items-center gap-4 lg:flex-col lg:items-center lg:gap-0">
      {/* Step number badge */}
      <div className="relative">
        <div className="absolute -right-1 -top-1 z-10 flex h-7 w-7 items-center justify-center rounded-full bg-accent text-xs font-bold text-primary shadow-sm ring-2 ring-primary/20 lg:-right-2 lg:-top-2 lg:h-8 lg:w-8 lg:text-sm">
          {step}
        </div>
        <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-md transition-all duration-300 group-hover:scale-105 group-hover:shadow-lg group-hover:ring-4 group-hover:ring-primary/20 lg:h-20 lg:w-20">
          <Icon className="h-7 w-7 lg:h-9 lg:w-9" />
        </div>
      </div>

      {/* Text content */}
      <div className="text-left lg:mx-auto lg:mt-4 lg:max-w-[180px] lg:text-center">
        <h3 className="text-lg font-semibold text-foreground">{title}</h3>
        <p className="text-sm leading-relaxed text-muted-foreground">{text}</p>
      </div>
    </div>
  );
}

export function ProcessSection() {
  return (
    <section className="py-24">
      <div className="container mx-auto px-6 text-start lg:text-center">
        <h2 className="mb-16 text-3xl font-bold sm:text-4xl">
          A{' '}
          <span className="relative inline-block before:absolute before:-inset-1 before:block before:-skew-y-[1.5deg] before:bg-primary/90 before:content-['']">
            <span className="relative text-white">Proven Process</span>
          </span>{' '}
          <br />
          <span className="mt-2 inline-block ps-3 lg:ps-0">
            to Build With{' '}
            <span className="relative inline-block before:absolute before:-inset-1 before:block before:-skew-y-[1.5deg] before:bg-primary/90 before:content-['']">
              <span className="relative text-white">Confidence</span>
            </span>
          </span>
        </h2>

        <div className="relative mx-auto max-w-5xl">
          {/* Desktop connecting line */}
          <div className="absolute left-[calc(12.5%+40px)] right-[calc(12.5%+40px)] top-10 hidden h-0.5 bg-gradient-to-r from-primary/10 via-primary/40 to-primary/10 lg:block" />

          {/* Mobile connecting line */}
          <div className="absolute bottom-8 left-8 top-8 w-0.5 bg-gradient-to-b from-primary/10 via-primary/40 to-primary/10 lg:hidden" />

          <div className="relative grid grid-cols-1 gap-10 lg:grid-cols-4 lg:gap-6">
            {steps.map((step) => (
              <TimelineStep key={step.step} {...step} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
